import { Toaster } from "@/components/ui/sonner";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useEffect, useState, Suspense, lazy } from "react";
import { Navbar, Footer } from "@/components/Layout";
import { AuthProvider } from "@/lib/auth";
import { useSearchVerification } from "@/lib/seoVerify";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { PenOverlay } from "@/components/PenOverlay";
import { BookLessonBubble } from "@/components/BookLessonBubble";
import { InstallAppPrompt } from "@/components/PwaActions";
// Eagerly load only the landing-shell pages users will see first on entry.
import ShelfPage from "@/pages/ShelfPage";
import LessonsPage from "@/pages/LessonsPage";
import NotFoundPage from "@/pages/NotFoundPage";
import "@/App.css";

// Heavy routes lazy-loaded so the Largest Contentful Paint on landing &
// SEO-indexable pages isn't blocked by the Mushaf reader's audio/Tajweed
// dependencies. Each splits into its own webpack chunk.
const MushafJuzzPage = lazy(() => import("@/pages/MushafJuzzPage"));
const MushafPage = lazy(() => import("@/pages/MushafPage"));
const SurahPage = lazy(() => import("@/pages/SurahPage"));
const SurahIndexPage = lazy(() => import("@/pages/SurahIndexPage"));
const JuzzInfoPage = lazy(() => import("@/pages/JuzzInfoPage"));
const JuzzIndexPage = lazy(() => import("@/pages/JuzzIndexPage"));
const BlogIndexPage = lazy(() => import("@/pages/BlogIndexPage"));
const BlogPostPage = lazy(() => import("@/pages/BlogPostPage"));
const SeoDebugPage = lazy(() => import("@/pages/SeoDebugPage"));
const CountryLessonsPage = lazy(() => import("@/pages/CountryLessonsPage"));
const ProgressPage = lazy(() => import("@/pages/ProgressPage"));
const LoginPage = lazy(() => import("@/pages/LoginPage"));
const RegisterPage = lazy(() => import("@/pages/RegisterPage"));
const NotesPage = lazy(() => import("@/pages/NotesPage"));
const AskBotPage = lazy(() => import("@/pages/AskBotPage"));
const GamePage = lazy(() => import("@/pages/GamePage"));
const AdminPage = lazy(() => import("@/pages/AdminPage"));
const SupportPage = lazy(() => import("@/pages/SupportPage"));

// Minimal skeleton shown while a code-split chunk loads. Keeps CLS at 0
// by reserving full viewport height.
const RouteSkeleton = () => (
  <div className="min-h-[60vh] flex items-center justify-center" aria-hidden>
    <div className="h-8 w-8 rounded-full border-2 border-primary border-t-transparent animate-spin" />
  </div>
);

function Shell({ children }) {
  const [penOpen, setPenOpen] = useState(false);
  const location = useLocation();
  useSearchVerification();

  // Hide the floating "Book Free Live Quran Classes" CTA on actual reading
  // pages (the Mushaf, Surah and Juzz views) so it never overlaps the Arabic
  // text. Shown everywhere else — landing, lessons, country, blog, etc.
  const isQuranReadingPage = location.pathname.startsWith("/quran/");

  // The platform's emergent-main.js script re-injects a "Made with Emergent"
  // badge into the DOM after page load (and ignores CSS overrides via inline
  // !important). The product owner has asked us to remove it, so we observe
  // the body and yank it as soon as it appears.
  useEffect(() => {
    const kill = () => {
      document.querySelectorAll('#emergent-badge, [id^="emergent-badge"]').forEach(el => el.remove());
    };
    kill();
    const obs = new MutationObserver(kill);
    obs.observe(document.body, { childList: true, subtree: true });
    return () => obs.disconnect();
  }, []);

  return (
    <div className="App min-h-screen flex flex-col bg-islamic-pattern">
      <Navbar onTogglePen={() => setPenOpen(o => !o)} penActive={penOpen} />
      <main className="flex-1">{children}</main>
      <Footer />
      <PenOverlay open={penOpen} onClose={() => setPenOpen(false)} />
      {!isQuranReadingPage && <BookLessonBubble />}
      <InstallAppPrompt />
      <Toaster richColors position="bottom-right" />
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Shell>
          <Suspense fallback={<RouteSkeleton />}>
            <Routes>
              <Route path="/" element={<ShelfPage />} />
              <Route path="/support" element={<SupportPage />} />
              <Route path="/lessons" element={<LessonsPage />} />
              <Route path="/online-quran-classes/:country" element={<CountryLessonsPage />} />
              <Route path="/quran/mushaf/juzz/:number" element={<MushafJuzzPage />} />
              <Route path="/quran/mushaf/page/:page" element={<MushafPage />} />
              <Route path="/quran/surahs" element={<SurahIndexPage />} />
              <Route path="/quran/surah/:slug" element={<SurahPage />} />
              <Route path="/quran/juzz" element={<JuzzIndexPage />} />
              <Route path="/quran/juzz/:number" element={<JuzzInfoPage />} />
              <Route path="/blog" element={<BlogIndexPage />} />
              <Route path="/blog/:slug" element={<BlogPostPage />} />
              <Route path="/_seo-debug" element={<SeoDebugPage />} />
              <Route path="/progress" element={<ProgressPage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/notes" element={<ProtectedRoute><NotesPage /></ProtectedRoute>} />
              <Route path="/ask" element={<ProtectedRoute><AskBotPage /></ProtectedRoute>} />
              <Route path="/game" element={<ProtectedRoute><GamePage /></ProtectedRoute>} />
              <Route path="/admin" element={<ProtectedRoute role="admin"><AdminPage /></ProtectedRoute>} />
              {/* Legacy redirects */}
              <Route path="/quran" element={<Navigate to="/" replace />} />
              <Route path="/quran/shelf" element={<Navigate to="/" replace />} />
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </Suspense>
        </Shell>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
